import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  Call,
  CallType,
  Event as GqlEvent,
  EventType,
  JustificationStatus,
  SimpleEvent,
} from '../../../generated/graphql';
import { AuthService } from '../../users/auth/auth.service';
import { NotificationsService } from '../../notifications/services/notifications.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { CallsService } from './calls.service';
import { RejectCallDialogComponent, RejectCallDialogResult } from './reject-call-dialog/reject-call-dialog.component';
import { ViewAttachmentsDialogComponent } from './view-attachments-dialog/view-attachments-dialog.component';

@Component({
  selector: 'app-calls',
  templateUrl: './calls.component.html',
  styleUrls: ['./calls.component.scss'],
})
export class CallsComponent implements OnInit {
  @Input()
  events: SimpleEvent[];

  @Input() showDocumentationActions: boolean = true;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  private pendingUploadCall: Call | null = null;

  audio: HTMLAudioElement;

  constructor(
    private auth: AuthService,
    private notificationsService: NotificationsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private callsService: CallsService,
  ) {}

  ngOnInit(): void {}

  approveCall(call: Call): void {
    this.callsService.approveCall(call.id).subscribe(result => {
      const updated = result?.data?.call;
      if (updated) {
        call.justificationStatus = updated.justificationStatus;
      }
    }, () => {
      this.snackBar.open('Error al aprobar la llamada', 'Cerrar', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    });
  }

  rejectCall(call: Call): void {
    this.dialog
      .open(RejectCallDialogComponent, { width: '400px' })
      .afterClosed()
      .subscribe((result: RejectCallDialogResult | undefined) => {
        if (!result) return;
        this.callsService.rejectCall(call.id, result.reason).subscribe(response => {
          const updated = response?.data?.call;
          if (updated) {
            call.justificationStatus = updated.justificationStatus;
            call.rejectionReason = updated.rejectionReason ?? null;
          }
        }, () => {
          this.snackBar.open('Error al rechazar la llamada', 'Cerrar', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
          });
        });
      });
  }

  uploadAttachment(call: Call): void {
    this.pendingUploadCall = call;
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: globalThis.Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.pendingUploadCall) {
      this.pendingUploadCall = null;
      return;
    }
    const callId = this.pendingUploadCall.id;
    this.pendingUploadCall = null;
    input.value = '';

    this.callsService.uploadAttachment(callId, file).subscribe(() => {
      this.snackBar.open('Documentación subida correctamente', 'Cerrar', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    }, () => {
      this.snackBar.open('Error al subir la documentación', 'Cerrar', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    });
  }

  viewAttachments(call: Call): void {
    this.dialog.open(ViewAttachmentsDialogComponent, {
      width: '600px',
      data: { callId: call.id },
    });
  }

  getRejectionTooltip(call: Call): string {
    if (call.justificationStatus === JustificationStatus.Rejected) {
      return call.rejectionReason ? call.rejectionReason : 'Sin motivo';
    }
    return call.justificationStatus;
  }

  justificationStatusIcon(status: JustificationStatus): string {
    switch (status) {
      case JustificationStatus.Accepted:
        return 'check-circle-outline';
      case JustificationStatus.Rejected:
        return 'close-circle-outline';
      case JustificationStatus.Pending:
      default:
        return 'clock-outline';
    }
  }

  justificationStatusColor(status: JustificationStatus): string {
    switch (status) {
      case JustificationStatus.Accepted:
        return 'green-text';
      case JustificationStatus.Rejected:
        return 'red-text';
      case JustificationStatus.Pending:
      default:
        return 'grey-text';
    }
  }

  get alertSevere(): boolean {
    return this.auth.alertSevere;
  }

  notifyNotAnswered(event: SimpleEvent) {
    if (!this.auth.alertNotAnswered) return false;

    return event.unattended;
  }

  callTypeText(simpleEvent: SimpleEvent) {
    if (simpleEvent['result']) {
      const call = simpleEvent as Call;
      switch (call.callType) {
        case CallType.Substitute:
          return 'Buscar substituto';
        case CallType.Interruption:
          return 'Interrupción';
        case CallType.List:
          return 'Lista';
        case CallType.Normal:
          return 'Guardia';
        case CallType.Remember:
          return 'Recordatorio';
      }
    } else {
      const event = simpleEvent as GqlEvent;

      switch (event.type) {
        case EventType.Api:
          return 'API';
        case EventType.WebCancelled:
          return 'Web';
        case EventType.WebChange:
          return 'Web';
        case EventType.WebSubstitution:
          return 'Web';
        case EventType.TelegramAcceptAssistance:
          return 'Telegram';
        case EventType.TelegramRejectAssistance:
          return 'Telegram';
        case EventType.TelegramRejectShift:
          return 'Telegram';
        case EventType.TelegramConfirmShift:
          return 'Telegram';
      }
    }
  }

  callTypeIcon(simpleEvent: SimpleEvent) {
    if (simpleEvent['result']) {
      const call = simpleEvent as Call;
      switch (call.callType) {
        case CallType.Substitute:
          return 'account-search';
        case CallType.Interruption:
          return 'cancel';
        case CallType.List:
          return 'account-details';
        case CallType.Normal:
          return 'account-clock';
        case CallType.Remember:
          return 'reminder';
      }
    } else {
      const event = simpleEvent as GqlEvent;

      switch (event.type) {
        case EventType.Api:
          return 'api';
        case EventType.WebCancelled:
          return 'account-cancel';
        case EventType.WebChange:
          return 'account-convert';
        case EventType.WebSubstitution:
          return 'account-search';
        case EventType.TelegramAcceptAssistance:
        case EventType.TelegramRejectAssistance:
        case EventType.TelegramRejectShift:
        case EventType.TelegramConfirmShift:
          return 'message-text';
      }
    }
  }

  sendSevereNotification(event: SimpleEvent) {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '90%',
        data: {
          title: 'Enviar aviso',
          content: `¿Confirmas que deseas enviar un aviso a ${event.lawyer.name} ${event.lawyer.lastName}?`,
        },
      })
      .afterClosed()
      .pipe(filter(r => r))
      .subscribe(_ => {
        this.notificationsService.callNotAnsweredSevere(event.id).subscribe(() => {
          this.snackBar.open('Notificación enviada', 'Aceptar', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
          });
        });
      });
  }

  sendThreateningNotification(event: SimpleEvent) {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '90%',
        data: {
          title: 'Enviar aviso',
          content: `¿Confirmas que deseas enviar un aviso a ${event.lawyer.name} ${event.lawyer.lastName}?`,
        },
      })
      .afterClosed()
      .pipe(filter(r => r))
      .subscribe(_ => {
        this.notificationsService.callNotAnsweredThreatening(event.id).subscribe(() => {
          this.snackBar.open('Notificación enviada', 'Aceptar', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
          });
        });
      });
  }

  sendNotificationNotAnswered(event: SimpleEvent) {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '90%',
        data: {
          title: 'Enviar aviso',
          content: `¿Confirmas que deseas enviar un aviso a ${event.lawyer.name} ${event.lawyer.lastName}?`,
        },
      })
      .afterClosed()
      .pipe(filter(r => r))
      .subscribe(_ => {
        if (event['result'] != undefined)
          this.notificationsService.callNotAnswered(event.id).subscribe(s => {
            this.snackBar.open('Notificación enviada', 'Aceptar', {
              verticalPosition: 'top',
              horizontalPosition: 'right',
            });
          });
        else {
          this.notificationsService.eventNotAnswered(event.id).subscribe(s => {
            this.snackBar.open('Notificación enviada', 'Aceptar', {
              verticalPosition: 'top',
              horizontalPosition: 'right',
            });
          });
        }
      });
  }
}